import React, { useState, useEffect, useCallback } from "react";
import "./ExperiencesTable.css";
import { PaginationResponse } from "../../../types";
import {
  Table,
  Pagination,
  Dropdown,
  type TableColumn,
  type DropdownOption,
} from "../../Common";
import { MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Typography, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { categoryService } from "../../../services";
import { handleApiError } from "../../../services/error.service";
import { useSmartPageSize } from "../../../config/pagination";
import type { OccasionLocale } from "../../../types/category.types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Display Experience type (mapped from API Occasion)
export interface DisplayExperience {
  experienceId: string;
  name: string;
  iconURL: string;
  description?: string;
  vendorCount: number;
  displayOrder: number; // Display order for sorting
  createdAt: string;
  updatedAt: string;
  locale?: OccasionLocale;
}

interface ExperiencesTableProps {
  searchQuery?: string;
  refreshTrigger?: number;
  onExperienceDeleted?: () => void;
  onEditExperience?: (experienceId: string, payload?: DisplayExperience) => void;
}

const ExperiencesTable: React.FC<ExperiencesTableProps> = ({
  searchQuery = "",
  refreshTrigger = 0,
  onExperienceDeleted,
  onEditExperience,
}) => {
  const [experiences, setExperiences] = useState<DisplayExperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log({ error });

  // Smart pagination based on screen size
  const [pagination, setPagination] = useState<PaginationResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const smartPageSize = useSmartPageSize("experiences");

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExperience, setSelectedExperience] = useState<DisplayExperience | null>(null);
  const open = Boolean(anchorEl);

  // Menu handlers
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    experience: DisplayExperience
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedExperience(experience);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedExperience(null);
  };

  const handleEdit = () => {
    if (selectedExperience && onEditExperience) {
      onEditExperience(selectedExperience.experienceId, selectedExperience);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedExperience) return;

    try {
      await categoryService.deleteExperience(selectedExperience.experienceId);
      toast.success("Experience deleted successfully");
      handleMenuClose();

      // Refetch the experiences list to update the table
      if (onExperienceDeleted) {
        onExperienceDeleted();
      } else {
        fetchExperiences(currentPage, searchQuery);
      }
    } catch (error: any) {
      console.error("Failed to delete experience:", error);
      toast.error(error?.response?.data?.message || "Failed to delete experience");
      handleMenuClose();
    }
  };

  const fetchExperiences = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getAllExperiences(page, smartPageSize, true);
      const occasions = response.data?.occasions || [];
      const apiPagination = response.data?.pagination;
      console.log("📊 ExperiencesTable: Loaded", occasions.length, "occasions");
      
      // Transform API occasions to display format
      const placeholder =
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTA5IiBoZWlnaHQ9IjEwOSIgdmlld0JveD0iMCAwIDEwOSAxMDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDkiIGhlaWdodD0iMTA5IiBmaWxsPSIjRjRGNEY0Ii8+CjxwYXRoIGQ9Ik01NC41IDMwQzQxLjUgMzAgMzEgNDAuNSAzMSA1My41QzMxIDY2LjUgNDEuNSA3NyA1NC41IDc3QzY3LjUgNzcgNzggNjYuNSA3OCA1My41Qzc4IDQwLjUgNjcuNSAzMCA1NC41IDMwWiIgc3Ryb2tlPSIjQzRDNEM0IiBzdHJva2Utd2lkdGg9IjIiLz4KPC9zdmc+Cg==';

      let displayExperiences: DisplayExperience[] = occasions.map((occasion) => ({
        experienceId: occasion.occasionId,
        name: occasion.title,
        iconURL: occasion.imageUrl || placeholder,
        description: occasion.description,
        vendorCount: 0,
        displayOrder: occasion.displayOrder || 999999, // Default for old records
        createdAt: occasion.createdAt,
        updatedAt: occasion.updatedAt,
        locale: occasion.locale,
      }));

      // Fetch vendor counts per occasion
      displayExperiences = await Promise.all(
        displayExperiences.map(async (exp) => {
          try {
            const countResp = await categoryService.getOccasionVendorCount(exp.experienceId);
            console.log("📊 ExperiencesTable: Vendor count response", countResp);
            return { ...exp, vendorCount: countResp.data.vendorCount };
          } catch (err) {
            console.error('Failed to fetch vendor count for occasion', exp.experienceId, err);
            return exp;
          }
        })
      );

      // Apply search filter (client-side for now, until API supports it)
      if (search.trim()) {
        const searchLower = search.toLowerCase();
        displayExperiences = displayExperiences.filter(
          (exp) =>
            exp.name.toLowerCase().includes(searchLower) ||
            exp.description?.toLowerCase().includes(searchLower)
        );
      }

      // Sort by displayOrder (ascending)
      displayExperiences.sort((a, b) => a.displayOrder - b.displayOrder);

      setExperiences(displayExperiences);

      // Use API pagination response
      setPagination({
        currentPage: apiPagination?.currentPage || page,
        totalPages: apiPagination?.totalPages || 1,
        totalItems: apiPagination?.totalItems || displayExperiences.length,
        hasMore: apiPagination?.hasMore || false,
      });
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [smartPageSize]);

  // Handle reorder
  const handleReorder = useCallback(async (reorderedExperiences: DisplayExperience[]) => {
    try {
      // Update local state immediately for optimistic UI
      setExperiences(reorderedExperiences);

      // Prepare reorder data with new displayOrder values
      const reorderData = reorderedExperiences.map((exp, index) => ({
        occasionId: exp.experienceId,
        displayOrder: index + 1, // 1-based indexing
      }));

      // Call API to persist the new order
      await categoryService.reorderExperiences(reorderData);
      toast.success('Experiences reordered successfully');
    } catch (err) {
      const errorMessage = handleApiError(err);
      toast.error(errorMessage);
      // Revert to original order on error
      fetchExperiences(currentPage, searchQuery);
    }
  }, [currentPage, searchQuery, fetchExperiences]);

  // Fetch experiences listing on mount and when page or search changes
  useEffect(() => {
    fetchExperiences(currentPage, searchQuery);
  }, [currentPage, searchQuery, smartPageSize, refreshTrigger, fetchExperiences]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Dropdown options
  const getDropdownOptions = (experience: DisplayExperience | null): DropdownOption[] => {
    if (!experience) return [];

    return [
      {
        label: "Edit",
        value: "edit",
        onClick: handleEdit,
      },
      {
        label: "Delete",
        value: "delete",
        onClick: handleDelete,
      },
    ];
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = experiences.findIndex((item) => item.experienceId === active.id);
      const newIndex = experiences.findIndex((item) => item.experienceId === over.id);
      
      const reordered = [...experiences];
      const [removed] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, removed);
      
      handleReorder(reordered);
    }
  };

  return (
    <Box className="experiences-table-wrapper">
      {/* Custom Table with Drag and Drop */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Table Header */}
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)', padding: 'var(--palette-light-3-value) 0', borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)' }}>
          <Box sx={{ width: '24px' }} />
          <Box sx={{ flex: 5 }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Experience
            </Typography>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Vendors
            </Typography>
          </Box>
          <Box sx={{ width: '56px' }} />
        </Box>

        {/* Table Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : experiences.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
              <Typography>No experiences found</Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={experiences.map((e) => e.experienceId)}
                strategy={verticalListSortingStrategy}
              >
                {experiences.map((experience) => (
                  <SortableExperienceRow
                    key={experience.experienceId}
                    experience={experience}
                    onMenuOpen={handleMenuOpen}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Box>

      {/* Pagination - Show even when loading */}
      {pagination && (pagination.totalPages ? pagination.totalPages > 0 : pagination.hasMore) && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages || 1}
          totalItems={pagination.totalItems || experiences.length}
          itemsPerPage={smartPageSize}
          onPageChange={(page) => setCurrentPage(page)}
          itemLabel="experiences"
          showItemCount={true}
          showPrevNext={true}
          disabled={loading}
        />
      )}

      {/* Action Dropdown */}
      <Dropdown
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        options={getDropdownOptions(selectedExperience)}
      />
    </Box>
  );
};

// Sortable Row Component for Experiences (module-level to prevent remount on state changes)
const SortableExperienceRow: React.FC<{
  experience: DisplayExperience;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, experience: DisplayExperience) => void;
}> = ({ experience, onMenuOpen }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: experience.experienceId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className="experiences-table-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--palette-light-4-value)',
        padding: '11px 0',
        borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
        backgroundColor: 'var(--palette-light-background-primary-value-hex)',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {/* Drag Handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'grab',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIndicatorIcon sx={{ width: '24px', height: '24px', color: 'var(--palette-light-text-secondary-value-hex)' }} />
      </Box>

      {/* Experience Cell */}
      <Box className="experiences-table-experience-cell">
        {/* Experience Image */}
        <Box
          component="img"
          src={experience.iconURL}
          alt={experience.name}
          className="experiences-table-experience-image-cell"
          onError={(e: any) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTA5IiBoZWlnaHQ9IjEwOSIgdmlld0JveD0iMCAwIDEwOSAxMDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDkiIGhlaWdodD0iMTA5IiBmaWxsPSIjRjRGNEY0Ii8+CjxwYXRoIGQ9Ik01NC41IDMwQzQxLjUgMzAgMzEgNDAuNSAzMSA1My41QzMxIDY2LjUgNDEuNSA3NyA1NC41IDc3QzY3LjUgNzcgNzggNjYuNSA3OCA1My41Qzc4IDQwLjUgNjcuNSAzMCA1NC41IDMwWiIgc3Ryb2tlPSIjQzRDNEM0IiBzdHJva2Utd2lkdGg9IjIiLz4KPC9zdmc+Cg==';
            e.target.style.objectFit = 'contain';
          }}
        />

        {/* Experience Info */}
        <Box className="experiences-table-experience-info-cell">
          <Typography className="experiences-table-experience-name-cell">
            {experience.name}
          </Typography>
        </Box>
      </Box>

      {/* Vendors Cell */}
      <Box className="experiences-table-vendors-cell">
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          {experience.vendorCount}
        </Typography>
      </Box>

      {/* Actions Cell */}
      <IconButton
        className="experiences-table-action-button"
        onClick={(e) => {
          e.stopPropagation();
          onMenuOpen(e, experience);
        }}
      >
        <MoreHorizIcon sx={{ fontSize: "20px" }} />
      </IconButton>
    </Box>
  );
};

export default ExperiencesTable;

